import {
  CONTRACTOR_CAPACITY_FIELDS,
  ContractorBumperUsersDto,
} from '@vatos-pas/common';
import { ContractorFormValues, ContractorMutationPayload } from '../containers';

export const BUMPERS_STILL_ASSIGNED_MESSAGE =
  'Remove the assigned bumpers before disabling the Bumper resource type.';

/**
 * Explicit bumper link changes for PATCH /contractor/:id/full, diffed against
 * the links loaded when the form opened. Returns undefined when nothing
 * changed so the key is omitted and the API leaves the links untouched.
 */
export function buildBumperUsersDelta(
  initialIds: readonly string[],
  selectedIds: readonly string[],
): ContractorBumperUsersDto | undefined {
  const initial = new Set(initialIds);
  const selected = new Set(selectedIds);
  const add = Array.from(selected).filter(id => !initial.has(id));
  const remove = Array.from(initial).filter(id => !selected.has(id));
  if (!add.length && !remove.length) return undefined;
  return { add, remove };
}

const TRADE_CAPACITY_MAP = [
  { toggle: 'hangerAvailable', capacity: 'hangerCapacity' },
  { toggle: 'finisherAvailable', capacity: 'finisherCapacity' },
  { toggle: 'sprayerAvailable', capacity: 'sprayerCapacity' },
  { toggle: 'trimAvailable', capacity: 'trimCapacity' },
  { toggle: 'paintAvailable', capacity: 'paintCapacity' },
  { toggle: 'bumperAvailable', capacity: 'bumperCapacity' },
] as const;

export function buildContractorPayload(
  values: ContractorFormValues,
): ContractorMutationPayload {
  const payload: ContractorMutationPayload = {
    name: values.name,
    corporation: values.corporation,
    paymentType: values.paymentType || undefined,
    paymentRate: values.paymentRate ?? undefined,
    bumperPaymentType: values.bumperPaymentType || undefined,
    bumperPaymentRate: values.bumperPaymentRate ?? undefined,
    hangerAvailable: values.hangerAvailable,
    finisherAvailable: values.finisherAvailable,
    sprayerAvailable: values.sprayerAvailable,
    trimAvailable: values.trimAvailable,
    paintAvailable: values.paintAvailable,
    bumperAvailable: values.bumperAvailable,
    specialistAvailable: values.specialistAvailable,
    active: values.active,
    suspended: values.suspended,
    canPerformHighCeilingTasks: values.canPerformHighCeilingTasks,
    notes: values.notes || undefined,
  };

  for (const { toggle, capacity } of TRADE_CAPACITY_MAP) {
    if (values[toggle]) {
      payload[capacity] = values[capacity];
    }
  }

  return payload;
}

/**
 * Regional Manager whitelist: users with only CAN_EDIT_CONTRACTOR_CAPACITY
 * may persist resource-type toggles and capacities, nothing else. Detail
 * inputs are visibly disabled, but react-hook-form still emits their
 * values — this trims them client-side; the API applies the same list
 * (CONTRACTOR_CAPACITY_FIELDS, shared through @vatos-pas/common) as the
 * authoritative cut on PATCH /contractor/:id/full.
 */
export function buildContractorCapacityOnlyPayload(
  values: ContractorFormValues,
): ContractorMutationPayload {
  const full = buildContractorPayload(values);
  const allowed: ContractorMutationPayload = {};
  for (const field of CONTRACTOR_CAPACITY_FIELDS) {
    if (field in full) {
      (allowed as Record<string, unknown>)[field] = (
        full as Record<string, unknown>
      )[field];
    }
  }
  return allowed;
}
